// Mirror TAY của backend/pkg/engines/payroll/salary_rules.go — mọi field TÊN
// và LỒNG NHAU phải khớp byte-for-byte với json tag Go. `schema.ts` (sinh từ
// openapi.yaml) không giúp được vì `config` khai `unknown` (mỗi phòng một
// hình dạng khác nhau) — đây là hợp đồng duy nhất giữ FE và Go struct đồng bộ.
//
// ⚠️ ĐỪNG đổi tên field ở đây khi chỉ muốn sửa NHÃN hiển thị — nhãn nằm ở
// department-metadata.ts, tách bạch khỏi field name để đổi nhãn không bao giờ
// làm lệch key JSON gửi lên PUT.

/**
 * PerClientRates dùng CHUNG cho cả photoAT VÀ makeupAT (struct Go gộp field
 * của cả hai phòng vào một type). Nghĩa là: JSON của photoAT.fulltime VẪN
 * chứa hairPS/perThayMakeup (luôn 0, không dùng), và JSON của
 * makeupAT.fulltime VẪN chứa perEditPhoto/perGhepPhong/perGhepAo/
 * perThayTrangPhuc/perComboInChinh (luôn 0, không dùng).
 *
 * department-metadata.ts CHỈ liệt kê field liên quan tới từng phòng trong
 * form — field "không liên quan" vẫn phải được GIỮ NGUYÊN giá trị hiện có
 * khi lưu (RuleTreeEditor sửa trên bản sao đầy đủ của config đã tải, không
 * dựng lại object từ đầu), nếu không sẽ âm thầm ghi đè field đó về 0.
 */
export interface PerClientRates {
  baseSalary: number;
  perClient: number;
  perCVBasic: number;
  perCVpro: number;
  // Bảy ô giá phát sinh (perEditPhoto … perThayMakeup) rời đi ngày 10/09/2026
  // chiều (QĐ 0115): mỗi phát sinh nay là MỘT dòng trong `surchargeRules` của
  // thẻ, mang giá cho cả ba loại hợp đồng.
}

export interface Milestone {
  threshold: number;
  bonus: number;
}

/**
 * BẢNG LUẬT PHÁT SINH (QĐ 0115) — mỗi thẻ vai một bảng, mỗi dòng MỘT phát
 * sinh: máy nhận bằng mã hay chữ, trả tiền hay công, giá cho cả ba loại hợp
 * đồng, mỗi mục là mấy đơn vị.
 *
 * Thứ tự trong mảng là thứ tự máy xét; mã xét TRƯỚC chữ trên toàn bảng.
 */
export interface SurchargeRates {
  fulltime: number;
  parttime: number;
  ctv: number;
}

/** Miễn theo loại buổi (chỉ phạm vi buổi chụp). freeQty 0 = BỎ HẲN. */
export interface SurchargeExempt {
  setKinds: string[];
  freeQty: number;
}

export type SurchargeRuleUnit = "vnd" | "cong" | "cong_chuan";
export type SurchargeRuleScope = "idphoto" | "profile" | "session";

export interface SurchargeRule {
  /** Khoá ổn định, duy nhất trong thẻ; là khoá gộp trên bản kê lương. */
  key: string;
  /** Tên hiện trên bản kê. */
  label: string;
  /** Mã mục danh mục — một mã chỉ ở MỘT rule trong thẻ. */
  codes: string[];
  /** Chữ dò trên nhãn, cho dòng gõ tay không mã (máy hạ chữ thường lúc ghi). */
  labels: string[];
  /** KHÔNG khớp khi nhãn chứa chữ này. */
  excludeLabels?: string[];
  scope: SurchargeRuleScope;
  unit: SurchargeRuleUnit;
  /** Đồng khi unit=vnd, số công khi unit=cong. */
  rates: SurchargeRates;
  /** Chỉ phạm vi profile: giá khi gói CAO CẤP. Vắng = như rates. */
  premiumRates?: SurchargeRates;
  /**
   * Tiền mỗi lần theo bậc tay nghề G1…G5 (thợ trang điểm buổi chụp, CEO chốt
   * 04/11/2026). Có ô này thì thợ có bậc nhận đúng mức của bậc mình, chia đều
   * số thợ; `rates` chỉ còn là mức dự phòng khi không đọc được bậc.
   */
  tierRates?: Record<string, number>;
  /** Chỉ phạm vi buổi chụp. */
  exempt?: SurchargeExempt;
  /**
   * Tỷ lệ công gốc (QĐ 0179). Có ô này thì buổi chụp từ mốc công chung tính
   * dòng bằng số mục × tỷ lệ × công gốc của đơn, nhân bậc, chia đều số thợ —
   * giá tiền của dòng chỉ còn là sàn. Chỉ phạm vi ảnh thẻ / profile.
   */
  baseUnitRatio?: number;
  /**
   * Nâng công của đơn lên MỨC của rule, thay vì cộng dồn thêm vào (QĐ 0194
   * điều 7). Chỉ hợp lệ khi unit="cong_chuan", và không dùng ở scope="session"
   * (bên đó công đi theo số mặt, nâng cả buổi là xoá phần của những mặt khác).
   */
  raise?: boolean;
}

export type SurchargeRuleBook = SurchargeRule[];

/** Xếp lớp đơn ảnh thẻ theo MÃ GÓI hay theo MÃ NHÓM — dùng chung cho cả ba lớp. */
export type ProductMatchBy = "product" | "line";

/** Danh sách mã của một lớp. Nghĩa của mã do `matchBy` quyết định. */
export interface ProductClassMembers {
  codes: string[];
}

/**
 * Xếp lớp đơn ảnh thẻ (AT · CV pro · CV cơ bản) để biết đơn ăn ô giá nào.
 * Photo AT và Makeup AT mỗi thẻ một bản (QĐ 0113); từ QĐ 0116 chọn từ danh mục
 * chứ không gõ tiền tố mã.
 */
export interface ProductClassRules {
  matchBy: ProductMatchBy;
  at: ProductClassMembers;
  cvPro: ProductClassMembers;
  cvBasic: ProductClassMembers;
}

/**
 * Định mức tháng của MỘT vai (quota.go `QuotaRules`) — chỉ ba thẻ Photo AT,
 * Makeup AT, Photo PF có. CHỈ XẾP HẠNG, không cộng trừ tiền. Hai ô sàn tính
 * bằng thạch lương thảo (không phải đồng); `caToiThieu` là tỷ lệ của số ca chuẩn.
 */
export interface QuotaRules {
  enabled: boolean;
  sanFulltime: number;
  sanParttime: number;
  caToiThieu: number;
}

export interface PhotoATRules {
  fulltime: PerClientRates;
  parttime: PerClientRates;
  ctv: PerClientRates;
  milestones: Milestone[];
  // Ba ô về thẻ này ngày 10/09/2026 (QĐ 0113) — luật chỉ thợ chụp ảnh thẻ đọc.
  products: ProductClassRules;
  /** Bảng luật phát sinh ảnh thẻ của thợ chụp (QĐ 0115). */
  surchargeRules: SurchargeRuleBook;
  /** Giờ vượt chuẩn tháng × (lương cứng ÷ chuẩn giờ) × hệ số này (1,5). */
  overtimeMultiplier: number;
  quota: QuotaRules;
}

export interface MakeupATRules {
  fulltime: PerClientRates;
  parttime: PerClientRates;
  ctv: PerClientRates;
  milestones: Milestone[];
  // Bốn ô về thẻ này ngày 10/09/2026 (QĐ 0113).
  products: ProductClassRules;
  /** Gói ảnh thẻ KHÔNG có trang điểm — thợ trang điểm không được tính khách. */
  makeupExcludedProducts: string[];
  /** Bảng luật phát sinh ảnh thẻ của thợ trang điểm. */
  surchargeRules: SurchargeRuleBook;
  overtimeMultiplier: number;
  quota: QuotaRules;
}

export interface ProductRate {
  product: string;
  /**
   * Số CÔNG của gói cho vai của thẻ (quyết định 0174) — số thực giữ ĐỦ chữ số.
   * Dương thì máy trả theo công và KHÔNG đọc ô `rate`. Bản lưu trước quyết định
   * ấy không có ô này, nên để tuỳ chọn.
   */
  units?: number;
  /** Đồng — chỉ còn ở dòng sổ cũ khai bằng tiền; màn hiện bằng thạch lương thảo. */
  rate: number;
}

/**
 * Số CÔNG của một đơn Profile theo hạng gói, cho MỘT vai (CEO chốt 20/09/2026).
 * Buổi chụp từ 01/10/2026: tiền = công ÷ số thợ cùng vai × đơn giá một công ×
 * hệ số bậc. Là số thực giữ ĐỦ chữ số (2/3, 5/6) — màn chỉ rút gọn khi hiện.
 */
export interface ProfileUnits {
  basic: number;
  premium: number;
}

export interface MakeupPFRules {
  /** Mức theo đơn — sàn của đơn Profile tính theo công, và là mức của đơn doanh nghiệp. */
  basic: number;
  premium: number;
  profileUnits: ProfileUnits;
  /**
   * Gói profile CƠ BẢN theo thợ trang điểm (QĐ 0174 lát 2). Gói profile không nằm
   * ở đây lẫn ở `premiumProducts` là chưa xếp hạng: buổi chụp từ 01/10/2026 tạm
   * tính theo gói cơ bản, báo vàng và chặn chốt tháng. Danh sách rỗng là hợp lệ.
   */
  basicProducts: string[];
  /** Gói profile CAO CẤP theo thợ trang điểm — bản riêng, thẻ Photo PF có bản của thợ chụp. */
  premiumProducts: string[];
  /** Mức trả theo gói — xem ProductRateTable. */
  productRates: ProductRate[];
  /** Bảng luật phát sinh của thợ trang điểm: đơn profile / DN và buổi chụp Chụp Ảnh. */
  surchargeRules: SurchargeRuleBook;
}

export interface PhotoPFBracket {
  bac1: number;
  bac2: number;
}

export interface PhotoPFRules {
  /** Mức theo đơn (hai mức 1 · 2) — sàn của đơn Profile tính theo công. */
  basic: PhotoPFBracket;
  caocap: PhotoPFBracket;
  profileUnits: ProfileUnits;
  dn1Buoi: number;
  dn1BuoiNC: number;
  scoutDN: number;
  /** Phần thợ chụp hưởng trên tiền trang phục phát sinh (0.1 = 10 %). */
  costumeExtraRate: number;
  /** Mã gói profile CƠ BẢN theo thợ chụp — xem MakeupPFRules.basicProducts. */
  basicProducts: string[];
  /** Mã gói profile được coi là CAO CẤP theo thợ chụp — chọn từ danh mục sản phẩm. */
  premiumProducts: string[];
  /** Mức trả theo gói — xem ProductRateTable. */
  productRates: ProductRate[];
  /** Bảng luật phát sinh của thợ chụp: đơn profile / DN và buổi chụp Chụp Ảnh. */
  surchargeRules: SurchargeRuleBook;
  quota: QuotaRules;
}

export interface SaleLeaderTier {
  threshold: number;
  rate: number;
}

export interface SaleLeaderRules {
  commissionTiers: SaleLeaderTier[];
}

export interface SaleBucketRules {
  commissionRate: number;
  first50MNoCommission: boolean;
}

export interface SaleRules {
  leader: SaleLeaderRules;
  fulltime: SaleBucketRules;
  parttime: SaleBucketRules;
  ctv: SaleBucketRules;
  /** Phần doanh thu đầu KHÔNG tính hoa hồng khi bucket bật `first50MNoCommission`. */
  noCommissionThreshold: number;
  /** Lương cứng sale bán thời gian đang thử việc nhân với hệ số này (0.85). */
  probationBaseRate: number;
  /** Sale không rõ loại hợp đồng ăn tỉ lệ này trên toàn bộ doanh thu. */
  fallbackCommissionRate: number;
}

export interface ReceptionRules {
  hourlyRate: number;
  upsaleAtCv: number;
  upsaleConcept2: number;
  upsaleClothing: number;
  upsaleDn1Buoi: number;
  upsaleDn1Ngay: number;
  /** Giờ vượt chuẩn tháng cộng thêm (giờ vượt × đơn giá giờ) × hệ số này (0,5). */
  overtimeExtraRate: number;
}

export interface StudioAssistantRules {
  /** Đơn giá một giờ ca đã duyệt, dùng chung cả phòng. */
  hourlyRate: number;
}

export interface RoleBaseRules {
  baseSalary: number;
}

export interface RetouchRules {
  perPhoto: number;
  perDesign: number;
  perAlbum: number;
  perComposite: number;
  leader: RoleBaseRules;
  ctv: RoleBaseRules;
}

export interface CSKHRules {
  extraPhotoRate: number;
  printRate: number;
  leader: RoleBaseRules;
  nv: RoleBaseRules;
}

export interface AccountingRules {
  baseSalary: number;
}

export interface MarketingRules {
  baseSalary: number;
}

export interface BODRules {
  coordinatorCommissionRate: number;
  /** Tháng ĐẦU TIÊN (YYYY-MM) không còn hoa hồng điều phối; rỗng = trả mãi. */
  coordinatorCommissionEndMonth: string;
}

/** Chuẩn giờ tháng + trần giờ một lần vào ca, dùng chung ba vai (QĐ 0105). Hệ số tăng ca ở thẻ từng vai. */
export interface OvertimeRules {
  standardHoursPerDay: number;
  standardDaysPerMonth: number;
  maxHoursPerCheckin: number;
}

export interface UpsellTier {
  threshold: number;
  rate: number;
}

export interface UpsellRules {
  commissionRate: number;
  /** Sắp GIẢM DẦN theo threshold — pickUpsellTier (Go) quét từ đầu mảng,
   * khớp đầu tiên ăn cả doanh thu; sắp sai thứ tự làm khớp NHẦM bậc mà
   * không lỗi nào báo. UI phải tự sắp lại mỗi lần sửa (TierListEditor.tsx). */
  tiers: UpsellTier[];
}

export interface MoneyMilestone {
  threshold: number;
  bonus: number;
}

export interface StreakTier {
  days: number;
  bonus: number;
}

export interface StreakRules {
  /** Tăng dần theo days, CỘNG DỒN (không phải "mốc cao nhất ăn hết"). */
  tiers: StreakTier[];
  cap: number;
  depts: string[];
}

export interface ChampionRules {
  bonus: number;
  minStaff: number;
  depts: string[];
  minOutput: Record<string, number>;
}

export interface MotivationRules {
  /** "YYYY-MM" — rỗng nghĩa là dùng mặc định phía server. */
  effectiveMonth: string;
  retouchPhotos: Milestone[];
  cskhRevenue: MoneyMilestone[];
  receptionUpsellCount: Milestone[];
  champion: ChampionRules;
  streak: StreakRules;
}

/**
 * "_global" là phòng giả — gộp các field top-level của Rules không thuộc phòng
 * vận hành nào: BOD, Upsell, Motivation, chuẩn giờ, phụ cấp ca trưởng
 * (departments.go:globalConfig, khoá ghim bởi payroll.GlobalRulesJSONTags).
 * Luật chỉ một vai đọc nằm ở thẻ vai đó (QĐ 0113).
 */
export interface GlobalConfig {
  bod: BODRules;
  upsell: UpsellRules;
  motivation: MotivationRules;
  overtime: OvertimeRules;
  shiftLeadAllowanceVnd: number;
}

/**
 * Sổ cấu hình bậc tay nghề (quyết định 0104) — mirror TAY của
 * backend/pkg/engines/payroll/craft_rules.go.
 *
 * Đây KHÔNG phải một phòng ban: nó là một sổ dùng chung cho mọi khối nghề
 * (chụp · trang điểm) trải trên nhiều phòng. Nằm cùng chỗ với biểu giá phòng
 * vì đi qua đúng bảng `salary_rules_v2` với department = "craft".
 */
export interface CraftTierScale {
  g1: number;
  g2: number;
  g3: number;
  g4: number;
  g5: number;
}

export interface CraftHeadcountBand {
  minHeadcount: number;
  maxHeadcount: number;
  units: number;
  splitByPhotographers: boolean;
}

export interface CraftExtraConceptBand {
  minExtraSets: number;
  ratio: number;
}

export interface CraftWorkUnits {
  individual: number;
  couple: number;
  familyPremium: CraftHeadcountBand[];
  familyBasic: CraftHeadcountBand[];
  outdoorMultiplier: number;
  halfDay: number;
  extraConceptBands: CraftExtraConceptBand[];
}

export interface CraftTransition {
  floorTier: string;
  floorUntil: string;
  tierWithoutCertificate: string;
  noOneDropsDuringFloor: boolean;
  warnDaysBefore: number;
}

export interface CraftRollout {
  enabled: boolean;
  departments: string[];
  blocks: string[];
  branches: string[];
}

/** Gói nào là loại buổi nào — khoá là mã gói trong danh mục. */
export interface CraftSetSpec {
  kind: string;
  outdoor?: boolean;
}

export interface CraftMakeupFaceUnits {
  adult: number;
  male: number;
  child: number;
}

export interface CraftMakeupRules {
  unitRate: number;
  faceUnits: CraftMakeupFaceUnits;
  packageFaces: Record<string, number>;
  outdoorFlatVnd: number;
  halfDayUnits: number;
}

/** Mã mục phát sinh Chụp Ảnh máy đọc — mỗi ô một danh sách mã danh mục. */
export interface CraftItemCodes {
  extraConceptIndividual: string[];
  extraConceptCouple: string[];
  extraConceptFamily: string[];
  makeupExtraFace: string[];
  makeupExtraChild: string[];
}

export interface CraftRules {
  rollout: CraftRollout;
  tierCoefficients: CraftTierScale;
  seniorityCoefficients: CraftTierScale;
  seniorityMonths: number;
  roundToVnd: number;
  workUnits: CraftWorkUnits;
  transition: CraftTransition;
  sessionUnitRate: number;
  makeup: CraftMakeupRules;
  setCatalog: Record<string, CraftSetSpec>;
  items: CraftItemCodes;
}

/**
 * Một nấc thưởng tiểu đội trưởng (`pkg/engines/salesteam` Tier). `uptoVnd` là
 * mốc TRÊN của nấc; 0 = không giới hạn và chỉ được ở nấc cuối. Phần doanh thu
 * nằm trong nấc nào ăn tỷ lệ của nấc đó.
 */
export interface SalesTeamTier {
  uptoVnd: number;
  rateBp: number;
}

/**
 * Bộ số cơ chế lương đội bán hàng (QĐ 0483) — mirror TAY của
 * `backend/pkg/engines/salesteam/rules.go` (thẻ "Phòng Kinh doanh", CEO 2a
 * 29/10/2026). Tỷ lệ và hệ số tính bằng PHẦN VẠN: 170 = 1,7%; 12.000 = ×1,2.
 * Số lưu trong tháng nào chỉ áp từ lương tháng sau.
 */
export interface SalesTeamRules {
  saleRateBp: number;
  upsellRateBp: number;
  sellerKeepBp: number;
  overBonusBp: number;
  qualityBothBp: number;
  qualityOneBp: number;
  qualityNoneBp: number;
  onTimeMinBp: number;
  satisfiedMinBp: number;
  surveyMinBp: number;
  surveyDeadlineDay: number;
  careNoticeDaysBefore: number;
  careSendPhotoDays: number;
  careFollowUpDays: number;
  purchase2Bp: number;
  purchase3Bp: number;
  purchase4PlusBp: number;
  keyItemBp: number;
  baseSalesVnd: number;
  baseCskhVnd: number;
  baseSquadLeadVnd: number;
  baseTeamLeadVnd: number;
  baseRegionLeadVnd: number;
  squadLeadTiers: SalesTeamTier[];
  teamLeadRateBp: number;
  regionLeadRateBp: number;
  faultsLosePocket: number;
  /** Lương cứng riêng từng người cũ (mã nhân sự → đồng). Máy thôi đọc từ 31/10/2026 (lương riêng ở hợp đồng HRM); bản lưu giữ nguyên. */
  staffBaseVnd?: Record<string, number>;
}

export type DepartmentKey =
  | "photoAT"
  | "makeupAT"
  | "makeupPF"
  | "photoPF"
  | "sale"
  | "sales_team"
  | "reception"
  | "studioAssistant"
  | "retouch"
  | "cskh"
  | "accounting"
  | "marketing"
  | "craft"
  | "_global";

export interface DepartmentConfigMap {
  photoAT: PhotoATRules;
  makeupAT: MakeupATRules;
  makeupPF: MakeupPFRules;
  photoPF: PhotoPFRules;
  sale: SaleRules;
  sales_team: SalesTeamRules;
  reception: ReceptionRules;
  studioAssistant: StudioAssistantRules;
  retouch: RetouchRules;
  cskh: CSKHRules;
  accounting: AccountingRules;
  marketing: MarketingRules;
  craft: CraftRules;
  _global: GlobalConfig;
}

// Thứ tự cố định dùng cho danh sách/menu chọn phòng trên màn hình — khớp
// allDepartments trong departments.go (11 phòng + Phòng Kinh doanh + _global + craft).
// `as const satisfies` (thay vì annotate thẳng `DepartmentKey[]`) để giữ đây
// là TUPLE — DEPARTMENT_ORDER[0] mới suy ra literal "photoAT" thay vì
// "DepartmentKey | undefined" dưới noUncheckedIndexedAccess (tsconfig bật cờ
// này), vì Tabs defaultValue cần một DepartmentKey THẬT chứ không phải
// optional. `satisfies` vẫn giữ nguyên kiểm tra mỗi phần tử đúng DepartmentKey.
export const DEPARTMENT_ORDER = [
  "photoAT",
  "makeupAT",
  "makeupPF",
  "photoPF",
  "sale",
  "sales_team",
  "reception",
  "studioAssistant",
  "retouch",
  "cskh",
  "accounting",
  "marketing",
  "_global",
  "craft",
] as const satisfies DepartmentKey[];

export const DEPARTMENT_LABEL: Record<DepartmentKey, string> = {
  photoAT: "Photo AT",
  makeupAT: "Makeup AT",
  makeupPF: "Makeup PF",
  photoPF: "Photo PF",
  sale: "Sale",
  sales_team: "Phòng Kinh doanh",
  reception: "Lễ Tân",
  studioAssistant: "Trợ lý Studio",
  retouch: "Retouch",
  cskh: "CSKH",
  accounting: "Kế Toán",
  marketing: "Marketing",
  craft: "Bậc tay nghề",
  _global: "Chung (BOD / Upsell / Động lực / Giờ công)",
};

export function isDepartmentKey(v: string): v is DepartmentKey {
  return (DEPARTMENT_ORDER as string[]).includes(v);
}
